import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { catchError, map, Observable } from 'rxjs';
import { Category } from 'src-ironside/repositories/category.repository';
import { ResponseListApi } from 'src-ironside/repositories/common.repository';
import {
    DynamicProductParam,
    ProductRepository,
} from 'src-ironside/repositories/product.repository';
import { FrameValidation, Trame2Validate } from '../components/frame-import/frame-import.component';
import {
    DynamicContacParam,
    DynamicReferencedFilterParam,
} from 'src-ironside/repositories/supplier.repository';
import { Department } from 'src-ironside/repositories/department.repository';
import { ValidateFileLogistic } from 'src-ironside/repositories/import-logistic';
import {
    FrameType,
    WorkflowLogisticVariablePayload,
} from '../components/logistic-import/models/export-logistic.interface';

@Injectable({
    providedIn: 'root',
})
export class DynamicDocumentService {
    baseUrl = 'dynamic-documents';

    constructor(
        private readonly _httpClient: HttpClient,
        private productRepository: ProductRepository,
    ) {}

    public findById(
        documentName: string,
        id: string,
        frontOfficeCall: boolean = false,
        showLoading = true,
    ): Observable<any> {
        const options: any = {};
        if (frontOfficeCall) {
            options.headers = new HttpHeaders().append('Frontend-Request', 'true');
        }
        return this._httpClient.get<any>(
            `/${this.baseUrl}/${documentName}/${id}${showLoading ? '' : '?no-show-loading'}`,
            options,
        );
    }

    public cloneById(
        documentName: string,
        id: string,
        frontOfficeCall: boolean = false,
    ): Observable<any> {
        const options: any = {};
        if (frontOfficeCall) {
            options.headers = new HttpHeaders().append('Frontend-Request', 'true');
        }
        return this._httpClient.get<any>(`/${this.baseUrl}/${documentName}/${id}/clone`, options);
    }

    public findByIds(
        documentName: string,
        ids: string[],
        frontOfficeCall: boolean = false,
        showLoading = true,
    ): Observable<any> {
        const options: any = {};
        if (frontOfficeCall) {
            options.headers = new HttpHeaders().append('Frontend-Request', 'true');
        }
        // Set the header and assign back to options.headers

        return this._httpClient.post<any>(
            `/${this.baseUrl}/${documentName}/ids${showLoading ? '' : '?no-show-loading'}`,
            ids,
            options,
        );
    }

    public find(
        dynamicDocumentRequest: any,
        frontOfficeCall: boolean = false,
        showLoading = true,
    ): Observable<any> {
        const options: any = {};
        if (frontOfficeCall) {
            options.headers = new HttpHeaders().append('Frontend-Request', 'true');
        }

        return this._httpClient.post<any[]>(
            `/${this.baseUrl}/${dynamicDocumentRequest.collectionName}/search${
                showLoading ? '' : '?no-show-loading'
            }`,
            dynamicDocumentRequest,
            options,
        );
    }

    public findLegalEntity(
        dynamicDocumentRequest: any,
        frontOfficeCall: boolean = false,
        showLoading = true,
    ): Observable<any> {
        const options: any = {};
        if (frontOfficeCall) {
            options.headers = new HttpHeaders().append('Frontend-Request', 'true');
        }
        return this._httpClient.post<any[]>(
            `/${this.baseUrl}/${dynamicDocumentRequest.collectionName}/dynamic-legal-entity-search${
                showLoading ? '' : '?no-show-loading'
            }`,
            dynamicDocumentRequest,
            options,
        );
    }

    public findProductSpecial(
        dynamicDocumentRequest: any,
        frontOfficeCall: boolean = false,
        showLoading = true,
    ): Observable<any> {
        const options: any = {};
        if (frontOfficeCall) {
            options.headers = new HttpHeaders().append('Frontend-Request', 'true');
        }

        return this._httpClient.post<any[]>(
            `/${this.baseUrl}/${dynamicDocumentRequest.collectionName}/search${
                showLoading ? '' : '?no-show-loading'
            }`,
            dynamicDocumentRequest,
            options,
        );

        // Temporary comment Chibuzo code
        // return this._httpClient.post<any[]>(
        //     `/${this.baseUrl}/${dynamicDocumentRequest.collectionName}/search-optimized${
        //         showLoading ? '' : '?no-show-loading'
        //     }`,
        //     dynamicDocumentRequest,
        //     options
        // );
    }

    public findByViewId(
        dynamicDocumentRequest: any,
        frontOfficeCall: boolean = false,
        showLoading = true,
    ): Observable<any> {
        const options: any = {};
        if (frontOfficeCall) {
            options.headers = new HttpHeaders().append('Frontend-Request', 'true');
        }
        return this._httpClient.post<any[]>(
            `/${this.baseUrl}/${dynamicDocumentRequest.collectionName}/search/views/${
                dynamicDocumentRequest.viewId
            }${showLoading ? '' : '?no-show-loading'}`,
            dynamicDocumentRequest,
            options,
        );
    }

    public cancelImportFile(documentName: string): Observable<any> {
        return this._httpClient.post<any>(
            `/${this.baseUrl}/${documentName}/cancel-import-file`,
            null,
        );
    }

    public importFile(
        documentName: string,
        file: File,
        fileId: string,
        importSpecialEndpoint?: string,
    ): Observable<any> {
        const formData: FormData = new FormData();
        formData.append('file-id', fileId);
        formData.append('file', file, file.name);

        const options: any = {};
        options.headers = new HttpHeaders();
        options.headers.set('Content-Type', 'multipart/form-data');
        options.headers.set('Accept', 'application/json');
        options.headers.set('Access-Control-Allow-Origin', '*');
        options.reportProgress = true;
        let url = `/${this.baseUrl}/${documentName}/import-file`;

        if (importSpecialEndpoint) {
            url = `/${this.baseUrl}/${documentName}/${importSpecialEndpoint}`;
        }

        return this._httpClient.post<any>(url, formData, options);
    }

    public exportFile(
        documentName: string,
        fileExtension: string,
        exportFileRequest: any,
    ): Observable<Blob> {
        return this._httpClient.post<Blob>(
            `/${this.baseUrl}/${documentName}/export-file/${fileExtension}`,
            exportFileRequest,
            { responseType: 'blob' as 'json' },
        );
    }

    public exportFileWithProgress(
        documentName: string,
        fileExtension: string,
        fileId: string,
        exportFileRequest: any,
    ): Observable<void> {
        return this._httpClient.post<void>(
            `/${this.baseUrl}/${documentName}/export-file/${fileExtension}/files/${fileId}`,
            exportFileRequest,
        );
    }

    public exportFileAsPageSize(
        documentName: string,
        fileExtension: string,
        page: number,
        pageSize: number,
        exportFileRequest: any,
    ): Observable<{ status: number; body: Blob }> {
        return this._httpClient
            .post<Blob>(
                `/${this.baseUrl}/${documentName}/export-file/${fileExtension}?page=${page}&pageSize=${pageSize}`,
                exportFileRequest,
                { responseType: 'blob' as 'json', observe: 'response' },
            )
            .pipe(
                map((response: HttpResponse<Blob>) => {
                    return {
                        status: response.status,
                        body: response.body as Blob,
                    };
                }),
            );
    }

    public validateFrame(frame2validate: Trame2Validate): Observable<FrameValidation> {
        return this._httpClient
            .post<FrameValidation>(`/${this.baseUrl}/tram/validate`, frame2validate)
            .pipe(
                catchError((err) => {
                    throw err;
                }),
            );
    }

    public exportFileWithContact(
        documentName: string,
        fileExtension: string,
        exportFileRequest: any,
    ): Observable<Blob> {
        return this._httpClient.post<Blob>(
            `/${this.baseUrl}/${documentName}/export-file-supplier/${fileExtension}`,
            exportFileRequest,
            { responseType: 'blob' as 'json' },
        );
    }

    public exportFileWithPageSize(
        documentName: string,
        fileExtension: string,
        exportFileRequest: any,
        page: number,
        pageSize: number,
    ): Observable<{ status: number; body: Blob; ref?: string }> {
        return this._httpClient
            .post<Blob>(
                `/${this.baseUrl}/${documentName}/export-file-supplier/${fileExtension}?page=${page}&pageSize=${pageSize}`,
                exportFileRequest,
                { responseType: 'blob' as 'json', observe: 'response' },
            )
            .pipe(
                map((response: HttpResponse<Blob>) => {
                    const headers = response.headers;
                    let ref: string;
                    const headerKeys = headers.keys();
                    headerKeys.forEach((key) => {
                        const value = headers.get(key);
                        if (key === 'link') {
                            const match = value?.match(/<([^>]+)>; rel="([^"]+)"/);
                            if (match && match[2]) {
                                ref = match[2];
                            }
                        }
                    });
                    return {
                        status: response.status,
                        body: response.body as Blob,
                        ref: ref!,
                    };
                }),
            );
    }

    public exportFileSupplierWithProgress(
        documentName: string,
        fileExtension: string,
        fileId: string,
        exportFileRequest: any,
    ): Observable<void> {
        return this._httpClient.post<void>(
            `/${this.baseUrl}/${documentName}/export-file-supplier/${fileExtension}/files/${fileId}`,
            exportFileRequest,
        );
    }

    public exportFileOnViewWithProgress(
        documentName: string,
        fileExtension: string,
        viewId: string,
        fileId: string,
        exportFileRequest: any,
    ): Observable<void> {
        return this._httpClient.post<void>(
            `/${this.baseUrl}/${documentName}/export-file/${fileExtension}/views/${viewId}/files/${fileId}`,
            exportFileRequest,
        );
    }

    public exportFileOnViewAndPageSize(
        documentName: string,
        fileExtension: string,
        viewId: string,
        page: number,
        pageSize: number,
        exportFileRequest: any,
    ): Observable<{ status: number; body: Blob; ref?: string }> {
        return this._httpClient
            .post<Blob>(
                `/${this.baseUrl}/${documentName}/export-file/${fileExtension}/views/${viewId}?page=${page}&pageSize=${pageSize}`,
                exportFileRequest,
                { responseType: 'blob' as 'json', observe: 'response' },
            )
            .pipe(
                map((response: HttpResponse<Blob>) => {
                    const headers = response.headers;
                    let ref: string;
                    const headerKeys = headers.keys();
                    headerKeys.forEach((key) => {
                        const value = headers.get(key);
                        if (key === 'link') {
                            const match = value?.match(/<([^>]+)>; rel="([^"]+)"/);
                            if (match && match[2]) {
                                ref = match[2];
                            }
                        }
                    });
                    return {
                        status: response.status,
                        body: response.body as Blob,
                        ref: ref!,
                    };
                }),
            );
    }

    public exportFileOnView(
        documentName: string,
        fileExtension: string,
        viewId: string,
        exportFileRequest: any,
    ): Observable<Blob> {
        return this._httpClient.post<Blob>(
            `/${this.baseUrl}/${documentName}/export-file/${fileExtension}/views/${viewId}`,
            exportFileRequest,
            { responseType: 'blob' as 'json' },
        );
    }

    public exportFileSupplierOnView(
        documentName: string,
        viewId: string,
        exportFileRequest: any,
    ): Observable<Blob> {
        return this._httpClient.post<Blob>(
            `/${this.baseUrl}/${documentName}/export-file-supplier/views/${viewId}`,
            exportFileRequest,
            { responseType: 'blob' as 'json' },
        );
    }

    public exportFileSupplierOnViewExtra(
        documentName: string,
        viewId: string,
        exportFileRequest: any,
    ): Observable<Blob> {
        return this._httpClient.post<Blob>(
            `/${this.baseUrl}/${documentName}/export-file/xlsx/views/${viewId}`,
            exportFileRequest,
            { responseType: 'blob' as 'json' },
        );
    }

    public deleteById(documentName: string, id: string): Observable<any> {
        return this._httpClient.delete<any>(`/${this.baseUrl}/${documentName}/${id}`);
    }

    public saveDocument(
        documentName: string,
        document: any,
        frontOfficeCall: boolean = false,
    ): Observable<any> {
        const options: any = {};
        if (frontOfficeCall) {
            options.headers = new HttpHeaders().append('Frontend-Request', 'true');
        }

        return this._httpClient.post<any>(`/${this.baseUrl}/${documentName}`, document, options);
    }

    uploadFile(file: File, endpoint: string, supplierName: string): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('supplierName', supplierName);

        return this._httpClient.post(`${this.baseUrl}/${endpoint}`, formData, {
            reportProgress: true,
            observe: 'events', // Permet de suivre la progression
        });
    }

    uploadMultipleFiles(
        files: File[],
        endpoint: string,
        supplierName: string,
        docType: any,
    ): Observable<any> {
        const formData = new FormData();
        formData.append('supplierName', supplierName);
        formData.append('docType', docType);
        files.forEach((file) => {
            formData.append('files', file);
        });

        return this._httpClient.post(`${this.baseUrl}/${endpoint}`, formData, {
            reportProgress: true,
            observe: 'events',
        });
    }

    getValue(value: any, tabField: string, lang: string = 'fr_FR') {
        return value?.[tabField]?.[lang]
            ? value?.[tabField]?.[lang]
            : value?.[tabField]
              ? value?.[tabField]
              : value?.id;
    }

    public getCategoryRootLevelStatusPublic(): Observable<any> {
        return this._httpClient.get<Category[]>(`/${this.baseUrl}/dynamic_category`);
    }

    public getCategoryAllLevelStatusPublic(
        supplierIds: string[],
        filedName: string,
    ): Observable<any> {
        let filter = [
            {
                fieldName: 'categoryLevelCategory',
                conditions: [
                    {
                        type: 'EQUALS',
                        value: '1',
                    },
                    {
                        type: 'EQUALS',
                        value: '2',
                    },
                    {
                        type: 'EQUALS',
                        value: '3',
                    },
                ],
            },
            {
                fieldName: filedName,
                conditions: [
                    {
                        type: 'IS_NOT_EMPTY',
                        value: '',
                    },
                ],
            },
        ];

        // logic to filter the categories that linked to the supplier
        if (supplierIds.length) {
            const conditions = supplierIds.map((supplierId) => ({
                type: 'EQUALS',
                value: supplierId,
            }));
            filter.push({
                fieldName: 'linkToSupplier',
                conditions: conditions,
            });
        }

        const payload = {
            collectionName: 'dynamic_category',
            filters: filter,
            sorts: {},
            page: 1,
            pageSize: 100000,
            locales: ['fr-FR'],
        };
        return this._httpClient.post(`/${this.baseUrl}/dynamic_category/search`, payload);
    }

    getListDynamicDocumentByCollectionName(params: DynamicProductParam) {
        return this._httpClient.post<ResponseListApi<any>>(
            `${this.baseUrl}/${params.collectionName}/search`,
            params,
        );
    }

    searchDynamicProductList(params: DynamicProductParam) {
        return this._httpClient.post<ResponseListApi<any>>(
            /* `dynamic-documents/dynamic_product/search` */ `dynamic-documents/dynamic-product/v2/parents/search-by-children`,
            params,
        );
    }

    getParentProductByChildrent(params: DynamicProductParam) {
        return this._httpClient
            .post<
                ResponseListApi<any>
            >(`dynamic-documents/dynamic-product/search-by-parent?no-show-loading`, params)
            .pipe(
                map((res) => {
                    return {
                        content: res?.content
                            ?.filter((product: any) => product.statusProduct?.value === 'VALIDE')
                            ?.map((product: any) => this.productRepository.convertProduct(product)),
                        meta: {
                            pageSize: res?.size,
                            totalPages: res?.totalPages,
                        },
                    };
                }),
            );
    }

    getCategoriesBySupplierName(codes: string[]) {
        const condition = codes.map((el) => ({
            type: 'EQUALS',
            value: el,
        }));
        const params = [
            {
                fieldName: 'linkToSupplier',
                conditions: condition,
            },
            {
                fieldName: 'linkProduct',
                conditions: [
                    {
                        type: 'IS_NOT_EMPTY',
                        value: '',
                    },
                ],
            },
        ];

        return this._httpClient.post<Category[]>(
            `dynamic-documents/dynamic_category/find-all`,
            params,
        );
    }

    getDynamicReferencedByFilter(params: DynamicReferencedFilterParam, collectionName: string) {
        return this._httpClient.post<any>(
            `/dynamic-documents/${collectionName}/search-by-referenced?no-show-loading`,
            params,
        );
    }

    getContactPage(params: DynamicContacParam) {
        return this._httpClient.post<any>(`/dynamic-documents/dynamic_contact/search`, params);
    }

    exportFileSuplier(documentName: string, fileExtension: string, payload: any): Observable<any> {
        const params = new HttpParams().set('page', '1').set('pageSize', '10000');

        return this._httpClient.post(
            `${this.baseUrl}/${documentName}/export-file-supplier/${fileExtension}`,
            payload,
            {
                params,
                responseType: 'blob',
            },
        );
    }

    exportProductByChildrent(type: string, payload: any): Observable<any> {
        return this._httpClient.post(
            `${this.baseUrl}/dynamic_product/export-product-by-children/${type}`,
            payload,
            {
                responseType: 'blob',
            },
        );
    }

    getAggregateCountDynamic(collection_name: string, payload: any): Observable<any> {
        return this._httpClient.post(
            `${this.baseUrl}/${collection_name}/aggregate-count?no-show-loading`,
            payload,
        );
    }

    listContact(ids: any[]): Observable<any> {
        return this._httpClient
            .post('/dynamic-documents/dynamic_natural_person/ids', ids)
            .pipe(map((res: any) => res));
    }

    exportProductByTemplate(body: any, typeExport: 'xlsx' | 'pdf'): Observable<any> {
        return this._httpClient.post(
            `/dynamic-documents/dynamic-product/export-file-by-template/${typeExport}`,
            body,
            {
                responseType: 'blob',
            },
        );
    }

    exportPhotosByProductId(productId: string): Observable<Blob> {
        return this._httpClient.post(
            `/dynamic-documents/dynamic-product/${productId}/export-photos`,
            {},
            { responseType: 'blob' },
        );
    }

      exportPhotosByProductIds(productIds: string[]): Observable<Blob> {
          return this._httpClient.post(
              `/dynamic-documents/dynamic-product/export-photos`,
              productIds,
              { responseType: 'blob' },
          );
      }

    /**
     * Get the status of the product by id
     * @param body - The body of the request
     * @param body.filters - The filters of the request
     * @param body.fields - The field name of the request
     * @param body.locales. - The conditions of the request
     * @param body.sorts - The type of the condition
     * @returns The status of the product
     */
    getStatusById(body: DynamicProductParam) {
        return this._httpClient.post<any>(`/dynamic-documents/dynamic-product/list-by-id`, body);
    }

    getDataParameterByDocumentName(body: DynamicProductParam, parameter: string) {
        return this._httpClient.post<any>(
            `/dynamic-documents/dynamic-product/list-by-id/${parameter}`,
            body,
        );
    }

    getDataParameterCountByDocumentName(body: DynamicProductParam, parameter: string) {
        return this._httpClient.post<any>(
            `/dynamic-documents/dynamic-product/${parameter}/count?no-show-loading`,
            body,
        );
    }

    getResourceParentById(body: DynamicProductParam) {
        return this._httpClient.post<Department[]>(
            `/dynamic-documents/dynamic-product/parents/resources`,
            body,
        );
    }

    getProductByLogisticVariable(body: DynamicProductParam, showLoading = true) {
        return this._httpClient.post(`dynamic-documents/dynamic-product/no-logistics`, body);
    }

    getProductNewByResource(body: DynamicProductParam) {
        return this._httpClient.post(`dynamic-documents/dynamic-product/home-page`, body);
    }

    exportTemplateLogistic(body: DynamicProductParam) {
        return this._httpClient.post(
            `dynamic-documents/dynamic-product/template/import-logistics`,
            body,
            {
                responseType: 'blob',
            },
        );
    }

    uploadFileLogistic(file: File, supplierName: string) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('supplierName', supplierName);
        return this._httpClient.post<any>(
            `dynamic-documents/frame_module/upload-frame/logistics?no-show-loading`,
            formData,
        );
    }

    validateFileLogistic(body: ValidateFileLogistic) {
        return this._httpClient.post<any>(
            `dynamic-documents/dynamic-import-logistic/validate?no-show-loading?no-show-loading`,
            body,
        );
    }

    workflowLogisticVariable(body: WorkflowLogisticVariablePayload) {
        return this._httpClient.post<any>(
            `dynamic-documents/dynamic_workflow_logistic_variable`,
            body,
        );
    }

    getFrameType() {
        return this._httpClient.get<Array<FrameType>>(`parametre/nomtable/FRAME_TYPE`);
    }

    getParentIds(body: DynamicProductParam) {
        return this._httpClient.post<string[]>(
            `dynamic-documents/dynamic-product/parent-ids`,
            body,
        );
    }

    /**
     * Get dynamic resources by product IDs and resource type
     * @param productIds - Array of product IDs to filter
     * @param typeOfResource - Array of resource types to filter (e.g., ['PHOTO'])
     * @param locales - Array of locales (default: ['en_GB'])
     * @param showLoading - Whether to show loading indicator (default: true)
     * @returns Observable with list of dynamic resources
     */
    listResourcesByProductIds(
        productIds: string[],
        typeOfResource: string[],
        locales: string[] = ['en_GB'],
        showLoading: boolean = true,
    ): Observable<any> {
        const payload = {
            filters: [
                {
                    fieldName: 'id',
                    conditions: [
                        {
                            type: 'IN',
                            value: productIds,
                        },
                    ],
                },
                {
                    fieldName: 'typeOfResource',
                    conditions: [
                        {
                            type: 'IN',
                            value: typeOfResource,
                        },
                    ],
                },
            ],
            fields: [],
            locales: locales,
            sorts: {
                referenceProduct: 'DESC',
                publishedOrder: 'DESC',
            },
        };

        return this._httpClient.post<any>(
            `/${this.baseUrl}/dynamic-resource/list-by-product-ids${
                showLoading ? '' : '?no-show-loading'
            }`,
            payload,
        );
    }

    /**
     * Count dynamic resources by product IDs grouped by type
     * @param productIds - Array of product IDs to filter
     * @param locales - Array of locales (default: ['en_GB'])
     * @param showLoading - Whether to show loading indicator (default: true)
     * @returns Observable with count of resources grouped by type
     */
    countResourcesByProductIdsGroupByType(
        productIds: string[],
        locales: string[] = ['en_GB'],
        showLoading: boolean = true,
    ): Observable<any> {
        const payload = {
            filters: [
                {
                    fieldName: 'id',
                    conditions: [
                        {
                            type: 'IN',
                            value: productIds,
                        },
                    ],
                },
            ],
            fields: [],
            locales: locales,
            sorts: {
                referenceProduct: 'DESC',
                publishedOrder: 'DESC',
            },
        };

        return this._httpClient.post<any>(
            `/${this.baseUrl}/dynamic-resource/count-by-product-ids-group-by-type${
                showLoading ? '' : '?no-show-loading'
            }`,
            payload,
        );
    }

    /**
     * Count logistics and battery data by parent product IDs
     * @param body - DynamicProductParam with filters containing parentIds
     * @param showLoading - Whether to show loading indicator (default: true)
     * @returns Observable with { logisticsCount, batteryCount, totalCount }
     */
    countLogisticsBattery(
        body: DynamicProductParam,
        showLoading: boolean = true,
    ): Observable<{ logisticsCount: number; batteryCount: number; totalCount: number }> {
        return this._httpClient.post<{
            logisticsCount: number;
            batteryCount: number;
            totalCount: number;
        }>(
            `/${this.baseUrl}/dynamic-product/count-logistics-battery${
                showLoading ? '' : '?no-show-loading'
            }`,
            body,
        );
    }
}
